import { Trans } from '@lingui/react/macro';
import { useCanListMyTimesheets, useHasStaffTimesheets } from '@sit/openapi';
import { config } from '@web/config/env';
import { entriesApprovalsRoute } from '@web/containers/EntriesApprovals/helpers/routes';
import { leaveApprovalsRoute } from '@web/containers/LeaveApprovals/helpers/routes';
import { rateCardRoute } from '@web/containers/RateCards/helpers/routes';
import { timeApprovalsRoute } from '@web/containers/TimeApprovals/helpers/routes';
import { timeClocksRoute } from '@web/containers/TimeClocks/helpers/routes';
import { Menu, MenuItem } from 'carbon-react/lib/components/menu';
import styled from 'styled-components';
import { DebugVersion } from '../DebugVersion';
import { useIsActiveTab } from './hooks/useIsActiveTab';

export type SecondaryNavTabs =
  | 'approvals'
  | 'data-sources'
  | 'entries-approvals'
  | 'leave'
  | 'leave-approvals'
  | 'rate-cards'
  | 'staff-leave'
  | 'staff-time-clocks'
  | 'staff-timesheets'
  | 'crew-timesheets'
  | 'time-approvals'
  | 'time-clocks'
  | 'timesheets';

interface SecondaryNavProps {
  onTabChange: (tab: SecondaryNavTabs) => void;
  hideMyTimesheetsTab: boolean;
  hideApprovalsTab: boolean;
}

interface ApprovalsNavigationProps {
  hideApprovalsTab?: boolean;
  clickHandler: (tab: SecondaryNavTabs) => (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => void;
}

interface TimeClockNavigationProps {
  clickHandler: (tab: SecondaryNavTabs) => (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => void;
}

const ApprovalsNavigation = ({ hideApprovalsTab, clickHandler }: ApprovalsNavigationProps) => {
  const { isActive } = useIsActiveTab();
  if (hideApprovalsTab) {
    return null;
  }
  return (
    <>
      <MenuItem
        data-pendo-id="time-approvals-tab"
        href={timeApprovalsRoute}
        selected={isActive('time-approvals')}
        onClick={clickHandler('time-approvals')}
      >
        <Trans>Approve timesheets</Trans>
      </MenuItem>
      <MenuItem
        data-pendo-id="entries-approvals-tab"
        href={entriesApprovalsRoute}
        selected={isActive('entries-approval')}
        onClick={clickHandler('entries-approvals')}
      >
        <Trans>Approve time entries</Trans>
      </MenuItem>
    </>
  );
};

const TimeClockNavigation = ({ clickHandler }: TimeClockNavigationProps) => {
  const { isActive } = useIsActiveTab();
  const { data: isStaff } = useHasStaffTimesheets();
  const hasMyTimeClocks = useCanListMyTimesheets();

  return (
    <>
      {hasMyTimeClocks && (
        <MenuItem
          data-pendo-id="time-clocks-tab"
          href={timeClocksRoute}
          selected={isActive('time-clocks')}
          onClick={clickHandler('time-clocks')}
        >
          <Trans>My time clocks</Trans>
        </MenuItem>
      )}
      {isStaff && (
        <MenuItem
          data-pendo-id="staff-time-clocks-tab"
          href={timeClocksRoute}
          selected={isActive('staff-time-clocks')}
          onClick={clickHandler('staff-time-clocks')}
        >
          <Trans>Staff time clocks</Trans>
        </MenuItem>
      )}
    </>
  );
};

const RateCardsNavigation = ({ clickHandler }: TimeClockNavigationProps) => {
  const { isActive } = useIsActiveTab();
  const { data: isStaff } = useHasStaffTimesheets();

  if (!isStaff) {
    return null;
  }

  return (
    <MenuItem data-pendo-id="rate-cards-tab" href={rateCardRoute} selected={isActive('rate-cards')} onClick={clickHandler('rate-cards')}>
      <Trans>Rate cards</Trans>
    </MenuItem>
  );
};

const Root = styled.div`
  background-color: var(--colorsUtilityYang100);
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing * 2}px;
  z-index: ${({ theme }) => theme.zIndex.aboveAll + 1};

  @media screen and (min-width: 1280px) {
    padding: 0 ${({ theme }) => theme.spacing * 5}px;
  }
`;

const SecondaryNav = ({ onTabChange, hideMyTimesheetsTab, hideApprovalsTab }: SecondaryNavProps) => {
  const { isActive, isExactActive } = useIsActiveTab();

  function createOnClick(tab: SecondaryNavTabs) {
    return (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onTabChange(tab);
    };
  }

  const { data: isStaff } = useHasStaffTimesheets();

  return (
    <Root>
      <Menu variant="white">
        {!hideMyTimesheetsTab && (
          <MenuItem
            data-pendo-id="timesheets-tab"
            href="/timesheets"
            selected={isActive('timesheets')}
            onClick={createOnClick('timesheets')}
          >
            <Trans>My timesheets</Trans>
          </MenuItem>
        )}
        {isStaff && (
          <MenuItem
            data-pendo-id="staff-timesheets-tab"
            href="/staff-timesheets"
            selected={isActive('staff-timesheets')}
            onClick={createOnClick('staff-timesheets')}
          >
            <Trans>Staff timesheets</Trans>
          </MenuItem>
        )}
        {isStaff && config.VITE_APP_SHOW_CREW_TIMESHEETS && (
          <MenuItem
            data-pendo-id="crew-timesheets-tab"
            href="/crew-timesheets"
            selected={isActive('crew-timesheets')}
            onClick={createOnClick('crew-timesheets')}
          >
            <Trans>Crew timesheets</Trans>
          </MenuItem>
        )}
        <ApprovalsNavigation clickHandler={createOnClick} hideApprovalsTab={hideApprovalsTab} />
        <TimeClockNavigation clickHandler={createOnClick} />
        <RateCardsNavigation clickHandler={createOnClick} />
        <MenuItem
          data-pendo-id="leave-tab"
          href="/leave"
          selected={isExactActive('leave') || isActive('leave/')}
          onClick={createOnClick('leave')}
        >
          <Trans>Leave</Trans>
        </MenuItem>
        {isStaff && (
          <>
            <MenuItem
              data-pendo-id="leave-approvals-tab"
              href={leaveApprovalsRoute}
              selected={isActive('leave-approvals')}
              onClick={createOnClick('leave-approvals')}
            >
              <Trans>Leave approvals</Trans>
            </MenuItem>
            <MenuItem
              data-pendo-id="staff-leave-tab"
              href="/staff-leave"
              selected={isActive('staff-leave')}
              onClick={createOnClick('staff-leave')}
            >
              <Trans>Staff leave</Trans>
            </MenuItem>
          </>
        )}
        <MenuItem
          data-pendo-id="data-sources-tab"
          href="/data-sources"
          selected={isActive('data-sources')}
          onClick={createOnClick('data-sources')}
        >
          <Trans>Time sources</Trans>
        </MenuItem>
      </Menu>
      <DebugVersion />
    </Root>
  );
};

export default SecondaryNav;
